import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Form, Modal, Row, Col } from 'react-bootstrap';
import DayScheduleContent from './DayScheduleContent';
import PacienteSearchSelect from './PacienteSearchSelect';
import {
  addMinutesToLocalDatetime,
  applySlotRangeToForm,
  detectarConflictos,
  findCitasAfterRange,
  getCitaFinDate,
  getPacNombre,
  getSlotRangeFromForm,
  ESTADO_CITA_COLOR,
  ESTADO_CITA_LABEL,
  ROLES_CITA_PROFESIONAL,
  resolveDefaultDoctorId,
  resolveDoctorForPaciente,
  makePendingPacienteId,
  titleCaseNombre,
  isCitaPacientePendiente,
  getCitaPacienteLabel,
  getMiembroEquipo,
  formatMiembroEquipoLabel,
} from '../../utils/citasHelpers';
import { showModalWarning } from '../../utils/sweetAlert';

/**
 * Modal crear / editar cita (misma UI que el calendario).
 * lockPacienteId: fija el paciente (ficha del paciente).
 */
export default function CitaFormModal({
  show,
  onHide,
  form,
  setForm,
  pacientes = [],
  equipo = [],
  citas = [],
  user,
  onSave,
  lockPacienteId = null,
  diaSeleccionado = null,
  mode = 'create',
  drawer = false,
}) {
  const isEdit = mode === 'edit';
  const lockedPatient = lockPacienteId != null && lockPacienteId !== '';

  const docId = form.idDoctorCita !== '' && form.idDoctorCita != null ? form.idDoctorCita : user?.id;
  const finPreview = form.fechaHoraFin && form.fechaHoraFin.length >= 16
    ? form.fechaHoraFin
    : (form.fechaHora && form.fechaHora.length >= 16 ? addMinutesToLocalDatetime(form.fechaHora, 30) : '');

  const conflictos = useMemo(() => {
    if (form.fechaHora && form.fechaHora.length >= 16 && finPreview && docId != null && docId !== '') {
      return detectarConflictos(citas, form.fechaHora, finPreview, docId, form.id || null);
    }
    return [];
  }, [citas, form.fechaHora, form.fechaHoraFin, form.idDoctorCita, form.id, finPreview, docId]);

  const pacienteNombre = useMemo(() => {
    if (!lockedPatient) return null;
    return getPacNombre(pacientes, lockPacienteId);
  }, [lockedPatient, lockPacienteId, pacientes]);

  const pacienteActual = useMemo(() => {
    const pid = lockedPatient ? lockPacienteId : form.idPaciente;
    if (!pid) return null;
    return pacientes.find((p) => String(p.id) === String(pid)) || null;
  }, [lockedPatient, lockPacienteId, form.idPaciente, pacientes]);

  useEffect(() => {
    if (!show || !lockedPatient) return;
    setForm((f) => (String(f.idPaciente) === String(lockPacienteId) ? f : { ...f, idPaciente: String(lockPacienteId) }));
  }, [show, lockedPatient, lockPacienteId, setForm]);

  /** Nueva cita: profesional del paciente asignado o usuario/equipo por defecto. */
  useEffect(() => {
    if (!show || isEdit || !equipo.length) return;
    if (pacienteActual) {
      const doc = resolveDoctorForPaciente(pacienteActual, user, equipo);
      if (doc) {
        setForm((f) => ({ ...f, idDoctorCita: doc }));
        return;
      }
    }
    const defaultDoc = resolveDefaultDoctorId(user, equipo);
    if (!defaultDoc) return;
    setForm((f) => {
      const valido = f.idDoctorCita && equipo.some((m) => String(m.customer_id) === String(f.idDoctorCita));
      if (valido) return f;
      return { ...f, idDoctorCita: defaultDoc };
    });
  }, [show, isEdit, user, equipo, pacienteActual, setForm]);

  const canSave = !!(form.idDoctorCita && (form.idPaciente || (form.pacienteNombre || '').trim()));
  const formPacientePendiente = isCitaPacientePendiente(form) || !!(form.pacienteNombre && !pacientes.find((p) => String(p.id) === String(form.idPaciente)));

  const profesionalSeleccionado = useMemo(
    () => getMiembroEquipo(equipo, form.idDoctorCita),
    [equipo, form.idDoctorCita],
  );

  const equipoOrdenado = useMemo(() => {
    const list = [...equipo];
    list.sort((a, b) => {
      const aMe = String(a.customer_id) === String(user?.id) ? 0 : 1;
      const bMe = String(b.customer_id) === String(user?.id) ? 0 : 1;
      if (aMe !== bMe) return aMe - bMe;
      const aClin = ROLES_CITA_PROFESIONAL.has(a.role) ? 0 : 1;
      const bClin = ROLES_CITA_PROFESIONAL.has(b.role) ? 0 : 1;
      if (aClin !== bClin) return aClin - bClin;
      return (a.full_name || '').localeCompare(b.full_name || '', 'es');
    });
    return list;
  }, [equipo, user?.id]);

  const [scheduleDay, setScheduleDay] = useState(() => {
    const base = diaSeleccionado || (form.fechaHora ? new Date(form.fechaHora) : new Date());
    const d = new Date(base);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const modalWasOpenRef = useRef(false);
  const [scheduleExpanded, setScheduleExpanded] = useState(true);

  const hasScheduleSelected = form.fechaHora?.length >= 16;
  const scheduleCollapsed = !isEdit && hasScheduleSelected && !scheduleExpanded;

  /** Solo al abrir el modal: fijar el día inicial. En «Mover cita» no pisar la navegación con flechas. */
  useEffect(() => {
    if (!show) {
      modalWasOpenRef.current = false;
      setScheduleExpanded(true);
      return;
    }
    if (modalWasOpenRef.current) return;
    modalWasOpenRef.current = true;

    const base = isEdit
      ? (form.fechaHora ? new Date(form.fechaHora) : diaSeleccionado || new Date())
      : (diaSeleccionado || (form.fechaHora ? new Date(form.fechaHora) : new Date()));
    const d = new Date(base);
    d.setHours(0, 0, 0, 0);
    setScheduleDay(d);
    if (!isEdit && form.fechaHora?.length >= 16) {
      setScheduleExpanded(false);
    }
  }, [show, isEdit, diaSeleccionado, form.fechaHora]);

  /** Nueva cita: si cambia el día del calendario con el modal ya abierto, seguir ese día. */
  useEffect(() => {
    if (!show || isEdit || !modalWasOpenRef.current || !diaSeleccionado) return;
    const d = new Date(diaSeleccionado);
    d.setHours(0, 0, 0, 0);
    setScheduleDay(d);
  }, [show, isEdit, diaSeleccionado]);

  const formSelectedRange = useMemo(
    () => getSlotRangeFromForm(form, scheduleDay),
    [form, scheduleDay],
  );

  const handlePickRangeBlocked = useCallback(
    (blocking, pacientesList) => {
      const names = blocking
        .map((c) => getCitaPacienteLabel(c, pacientesList))
        .slice(0, 4)
        .join(', ');
      const extra = blocking.length > 4 ? ` y ${blocking.length - 4} más` : '';
      void showModalWarning(
        'Horario ocupado',
        `No se puede mover ahí: ya hay cita(s) activa(s) en ese horario (${names}${extra}). Elegí otra franja libre o cancelá la cita que ocupa el bloque.`,
      );
    },
    [],
  );

  const handlePickRange = useCallback(
    (date, startSlot, endSlot) => {
      setForm((f) => applySlotRangeToForm(f, date, startSlot, endSlot));
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      setScheduleDay(d);
      if (!isEdit) setScheduleExpanded(false);
    },
    [setForm, isEdit],
  );

  const citasDespues = useMemo(() => {
    if (!isEdit || !form.fechaHoraFin || form.fechaHoraFin.length < 16) return [];
    return findCitasAfterRange(citas, scheduleDay, form.fechaHoraFin, form.id);
  }, [isEdit, citas, scheduleDay, form.fechaHoraFin, form.id]);

  const handleScheduleDayChange = useCallback(
    (nextDay) => {
      setScheduleDay(nextDay);
      if (!form.fechaHora || form.fechaHora.length < 16) return;
      const dayStr = [
        nextDay.getFullYear(),
        String(nextDay.getMonth() + 1).padStart(2, '0'),
        String(nextDay.getDate()).padStart(2, '0'),
      ].join('-');
      setForm((f) => ({
        ...f,
        fechaHora: `${dayStr}T${f.fechaHora.slice(11, 16)}`,
        fechaHoraFin: `${dayStr}T${(f.fechaHoraFin || f.fechaHora).slice(11, 16)}`,
      }));
    },
    [form.fechaHora, setForm],
  );

  const scheduleHint = isEdit
    ? 'Mové la cita a un bloque libre. Si cancelan otra visita, ese horario queda disponible.'
    : 'Elegí el rango de horario en la grilla (30 min por fila)';

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered={!drawer}
      size="lg"
      className={`consultorio-modal citas-nueva-modal${drawer ? ' citas-nueva-modal--drawer' : ''}`}
      dialogClassName={drawer ? 'citas-drawer-dialog' : undefined}
      backdrop="static"
      animation={false}
      enforceFocus={false}
    >
      <Modal.Header closeButton className="citas-nueva-modal-header">
        <div className="citas-nueva-modal-heading">
          <Modal.Title className="citas-nueva-modal-title">
            <span className="citas-nueva-modal-title-icon" aria-hidden>
              <i className={`bi ${isEdit ? 'bi-arrows-move' : 'bi-calendar-plus-lg'}`} />
            </span>
            <span className="citas-nueva-modal-title-text">
              {isEdit ? 'Mover cita' : 'Nueva cita'}
              <small className="citas-nueva-modal-title-sub">
                {isEdit
                  ? 'Cambiá de día u horario en la grilla; las citas canceladas liberan espacio'
                  : 'Agenda una visita en el consultorio'}
              </small>
            </span>
          </Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body className="citas-nueva-modal-body p-0">
        <div className="citas-nueva-modal-inner">
          <div className="citas-nueva-preview">
            <div className="citas-nueva-preview-main">
              <div className="citas-nueva-preview-label">
                <i className="bi bi-calendar-event" aria-hidden />
                Resumen
              </div>
              <div className="citas-nueva-preview-value">
                {form.fechaHora?.length >= 16 ? (
                  <span className="citas-nueva-preview-datetime" title={new Date(form.fechaHora).toLocaleString('es-EC')}>
                    <strong className="citas-nueva-preview-date">
                      {new Date(form.fechaHora).toLocaleDateString('es-EC', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </strong>
                    <span className="citas-nueva-preview-sep">·</span>
                    <span className="citas-nueva-preview-time">
                      {form.fechaHora.slice(11, 16)}
                      {' → '}
                      {(form.fechaHoraFin && form.fechaHoraFin.length >= 16
                        ? form.fechaHoraFin.slice(11, 16)
                        : addMinutesToLocalDatetime(form.fechaHora, 30).slice(11, 16))}
                    </span>
                  </span>
                ) : (
                  <span className="citas-nueva-preview-placeholder">Elige fecha y horario</span>
                )}
              </div>
            </div>
            {!isEdit && diaSeleccionado && (
              <div className="citas-nueva-preview-chip" title="Día seleccionado desde el calendario">
                <i className="bi bi-pin-map-fill" aria-hidden />
                {diaSeleccionado.toLocaleDateString('es-EC', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                })}
              </div>
            )}
            {lockedPatient && pacienteNombre && (
              <div className="citas-nueva-preview-chip" title="Paciente">
                <i className="bi bi-person-fill" aria-hidden />
                {pacienteNombre}
              </div>
            )}
          </div>

          {conflictos.length > 0 && (
            <div className="citas-conflict-alert">
              <i className="bi bi-exclamation-triangle-fill" aria-hidden />
              <div>
                <strong>Solape en la agenda</strong>
                <div className="citas-conflict-list">
                  {conflictos.map((c) => (
                    <div key={c.id} className="citas-conflict-item">
                      <span>
                        {new Date(c.fechaHora).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
                        {' – '}
                        {getCitaFinDate(c).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className="citas-conflict-dot">·</span>
                      <span>{getPacNombre(pacientes, c.idPaciente)}</span>
                      <span className={`status-pill status-pill--${ESTADO_CITA_COLOR[c.estado] || 'secondary'}`}>
                        {ESTADO_CITA_LABEL[c.estado] || c.estado}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="citas-conflict-hint">
                  Mismo profesional: horarios que se cruzan. Puedes guardar igualmente si corresponde.
                </p>
              </div>
            </div>
          )}

          <div className="citas-nueva-cards">
            <section
              className={[
                'citas-nueva-card',
                'citas-nueva-card--schedule',
                scheduleCollapsed ? 'citas-nueva-card--schedule-collapsed' : '',
              ].filter(Boolean).join(' ')}
            >
              <header className="citas-nueva-card-head citas-nueva-card-head--with-action">
                <span className="citas-nueva-card-icon"><i className="bi bi-clock-history" aria-hidden /></span>
                <div className="citas-nueva-card-head__text">
                  <h4 className="citas-nueva-card-title">{isEdit ? 'Mover a otro horario' : 'Fecha y horario'}</h4>
                  <p className="citas-nueva-card-desc">
                    {scheduleCollapsed
                      ? 'Horario elegido. Completá paciente y motivo abajo, o expandí para cambiar la franja.'
                      : isEdit
                        ? 'Elegí día con las flechas y arrastrá el nuevo rango. No podés pisar otra cita activa.'
                        : 'Seleccioná el bloque o arrastrá varias franjas de 30 minutos'}
                  </p>
                </div>
                {!isEdit && hasScheduleSelected ? (
                  <button
                    type="button"
                    className="citas-schedule-toggle-btn"
                    onClick={() => setScheduleExpanded((v) => !v)}
                    aria-expanded={scheduleExpanded}
                  >
                    <i className={`bi ${scheduleExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden />
                    <span>{scheduleExpanded ? 'Reducir' : 'Expandir'}</span>
                  </button>
                ) : null}
              </header>

              {scheduleCollapsed ? (
                <div className="citas-schedule-selected-strip">
                  <div className="citas-schedule-selected-strip__icon" aria-hidden>
                    <i className="bi bi-clock-fill" />
                  </div>
                  <div className="citas-schedule-selected-strip__text">
                    <strong className="citas-schedule-selected-strip__date">
                      {new Date(form.fechaHora).toLocaleDateString('es-EC', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </strong>
                    <span className="citas-schedule-selected-strip__time">
                      {form.fechaHora.slice(11, 16)}
                      {' → '}
                      {(form.fechaHoraFin && form.fechaHoraFin.length >= 16
                        ? form.fechaHoraFin.slice(11, 16)
                        : addMinutesToLocalDatetime(form.fechaHora, 30).slice(11, 16))}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="citas-schedule-expand-btn"
                    onClick={() => setScheduleExpanded(true)}
                  >
                    <i className="bi bi-arrows-angle-expand" aria-hidden />
                    Cambiar horario
                  </button>
                </div>
              ) : (
              <div className="citas-form-schedule-picker">
                <DayScheduleContent
                  date={scheduleDay}
                  onDateChange={handleScheduleDayChange}
                  citas={citas}
                  pacientes={pacientes}
                  equipo={equipo}
                  schedulingHint={scheduleHint}
                  highlightPacienteId={lockPacienteId || form.idPaciente || null}
                  pickRangeMode
                  embedInForm
                  closeOnSlotSelect={false}
                  onPickRange={handlePickRange}
                  onPickRangeBlocked={(blocking) => handlePickRangeBlocked(blocking, pacientes)}
                  excludeCitaId={isEdit ? form.id : null}
                  formSelectedRange={formSelectedRange}
                  compactHeader
                />
                {isEdit && citasDespues.length > 0 && (
                  <div className="citas-move-after-hint citas-move-after-hint--in-grid">
                    <i className="bi bi-info-circle" aria-hidden />
                    <div>
                      <strong>Después en este día</strong>
                      <p className="mb-0">
                        {citasDespues.slice(0, 3).map((c, i) => (
                          <span key={c.id}>
                            {i > 0 ? ' · ' : ''}
                            {getPacNombre(pacientes, c.idPaciente)}{' '}
                            {new Date(c.fechaHora).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ))}
                        {citasDespues.length > 3 ? ` (+${citasDespues.length - 3})` : ''}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              )}

              {!scheduleCollapsed ? (
              <details className="citas-form-manual-times">
                <summary className="citas-form-manual-times__summary">
                  <i className="bi bi-sliders" aria-hidden />
                  Ajuste manual de fecha y hora
                </summary>
              <Row className="g-3 citas-nueva-card-grid citas-nueva-card-grid--manual">
                <Col xs={12} md={6}>
                  <Form.Group className="citas-nueva-field">
                    <Form.Label className="citas-nueva-label">
                      <i className="bi bi-calendar3" aria-hidden /> Fecha
                    </Form.Label>
                    <Form.Control
                      type="date"
                      className="citas-nueva-input"
                      value={form.fechaHora ? form.fechaHora.slice(0, 10) : ''}
                      onChange={(e) => {
                        const day = (e.target.value || '').slice(0, 10);
                        setForm((f) => ({
                          ...f,
                          fechaHora: `${day}T${f.fechaHora?.slice(11, 16) || '09:00'}`,
                          fechaHoraFin: `${day}T${f.fechaHoraFin?.slice(11, 16) || '09:30'}`,
                        }));
                        const d = new Date(`${day}T12:00:00`);
                        d.setHours(0, 0, 0, 0);
                        setScheduleDay(d);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <div className="citas-nueva-time-pair">
                    <Form.Group className="citas-nueva-field citas-nueva-field--time">
                      <Form.Label className="citas-nueva-label">Inicio</Form.Label>
                      <Form.Control
                        type="time"
                        className={`citas-nueva-input ${conflictos.length > 0 ? 'border-warning' : ''}`}
                        value={form.fechaHora ? form.fechaHora.slice(11, 16) : '09:00'}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          fechaHora: `${(f.fechaHora || '').slice(0, 10)}T${e.target.value || '09:00'}`,
                        }))}
                      />
                    </Form.Group>
                    <span className="citas-nueva-time-arrow" aria-hidden><i className="bi bi-arrow-right" /></span>
                    <Form.Group className="citas-nueva-field citas-nueva-field--time">
                      <Form.Label className="citas-nueva-label">Fin</Form.Label>
                      <Form.Control
                        type="time"
                        className={`citas-nueva-input ${conflictos.length > 0 ? 'border-warning' : ''}`}
                        value={
                          form.fechaHoraFin
                            ? form.fechaHoraFin.slice(11, 16)
                            : (form.fechaHora ? addMinutesToLocalDatetime(form.fechaHora, 30).slice(11, 16) : '09:30')
                        }
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          fechaHoraFin: `${(f.fechaHora || '').slice(0, 10)}T${e.target.value || '09:30'}`,
                        }))}
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>
              </details>
              ) : null}
            </section>

            <section className="citas-nueva-card">
              <header className="citas-nueva-card-head">
                <span className="citas-nueva-card-icon citas-nueva-card-icon--mint"><i className="bi bi-people-fill" aria-hidden /></span>
                <div>
                  <h4 className="citas-nueva-card-title">Paciente y profesional</h4>
                  <p className="citas-nueva-card-desc">
                    Quién atiende y quién es la visita. El administrador puede figurar como profesional a cargo.
                  </p>
                </div>
              </header>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  {lockedPatient ? (
                    <Form.Group className="citas-nueva-field">
                      <Form.Label className="citas-nueva-label">
                        <i className="bi bi-person-heart" aria-hidden /> Paciente
                      </Form.Label>
                      <Form.Control className="citas-nueva-input" value={pacienteNombre || 'Paciente'} readOnly disabled />
                    </Form.Group>
                  ) : (
                    <Form.Group className="citas-nueva-field">
                      <Form.Label className="citas-nueva-label">
                        <i className="bi bi-person-heart" aria-hidden /> Paciente <span className="text-danger">*</span>
                      </Form.Label>
                      <PacienteSearchSelect
                        pacientes={pacientes}
                        value={form.pacientePendiente ? '' : form.idPaciente}
                        pendingNombre={form.pacientePendiente ? (form.pacienteNombre || '') : ''}
                        allowCreatePending
                        onChange={(id) => {
                          if (!id) {
                            setForm((f) => ({
                              ...f,
                              idPaciente: '',
                              pacienteNombre: '',
                              pacientePendiente: false,
                            }));
                            return;
                          }
                          const p = pacientes.find((x) => String(x.id) === String(id));
                          const doc = p ? resolveDoctorForPaciente(p, user, equipo) : '';
                          setForm((f) => ({
                            ...f,
                            idPaciente: id,
                            pacienteNombre: '',
                            pacientePendiente: false,
                            ...(doc ? { idDoctorCita: doc } : {}),
                          }));
                        }}
                        onSelectPending={(nombre) => {
                          const neat = titleCaseNombre(nombre);
                          setForm((f) => ({
                            ...f,
                            idPaciente: makePendingPacienteId(neat),
                            pacienteNombre: neat,
                            pacientePendiente: true,
                          }));
                        }}
                        placeholder="Buscar o escribir paciente nuevo..."
                      />
                      {formPacientePendiente ? (
                        <p className="citas-nueva-pending-hint mb-0 mt-2">
                          <i className="bi bi-info-circle me-1" aria-hidden />
                          Paciente nuevo · pendiente de registrar en la ficha. La cita se guarda igual con el nombre.
                        </p>
                      ) : null}
                    </Form.Group>
                  )}
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="citas-nueva-field">
                    <Form.Label className="citas-nueva-label">
                      <i className="bi bi-person-badge" aria-hidden /> Profesional a cargo
                      {String(user?.id) === String(form.idDoctorCita) ? (
                        <span className="citas-nueva-label-tag">Vos</span>
                      ) : null}
                    </Form.Label>
                    {equipo.length <= 1 ? (
                      <Form.Control
                        className="citas-nueva-input"
                        value={formatMiembroEquipoLabel(profesionalSeleccionado || equipo[0])}
                        readOnly
                        disabled
                      />
                    ) : (
                      <Form.Select
                        className="citas-nueva-input"
                        value={form.idDoctorCita}
                        onChange={(e) => setForm((f) => ({ ...f, idDoctorCita: e.target.value }))}
                      >
                        <option value="">Seleccionar profesional...</option>
                        {equipoOrdenado.map((m) => (
                          <option key={m.customer_id} value={String(m.customer_id)}>
                            {formatMiembroEquipoLabel(m)}
                            {String(m.customer_id) === String(user?.id) ? ' · vos' : ''}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </section>

            {isEdit && (
              <section className="citas-nueva-card">
                <header className="citas-nueva-card-head">
                  <span className="citas-nueva-card-icon"><i className="bi bi-flag-fill" aria-hidden /></span>
                  <div>
                    <h4 className="citas-nueva-card-title">Estado</h4>
                    <p className="citas-nueva-card-desc">Pendiente, confirmada, realizada o cancelada</p>
                  </div>
                </header>
                <Form.Select
                  className="citas-nueva-input"
                  value={form.estado || 'pendiente'}
                  onChange={(e) => setForm((f) => ({ ...f, estado: e.target.value }))}
                >
                  {Object.entries(ESTADO_CITA_LABEL).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </Form.Select>
              </section>
            )}

            <section className="citas-nueva-card citas-nueva-card--notes">
              <header className="citas-nueva-card-head">
                <span className="citas-nueva-card-icon citas-nueva-card-icon--gold"><i className="bi bi-journal-medical" aria-hidden /></span>
                <div>
                  <h4 className="citas-nueva-card-title">Motivo y notas</h4>
                  <p className="citas-nueva-card-desc">Opcional · útil para preparar la consulta</p>
                </div>
              </header>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Group className="citas-nueva-field">
                    <Form.Label className="citas-nueva-label">Motivo de la visita</Form.Label>
                    <Form.Control
                      className="citas-nueva-input"
                      value={form.motivo}
                      onChange={(e) => setForm((f) => ({ ...f, motivo: e.target.value }))}
                      placeholder="Ej. limpieza, control, urgencia..."
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group className="citas-nueva-field mb-0">
                    <Form.Label className="citas-nueva-label">Notas internas</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      className="citas-nueva-textarea"
                      value={form.notas}
                      onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
                      placeholder="Recordatorios para el equipo"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </section>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="citas-nueva-modal-footer">
        <button type="button" className="citas-nueva-btn citas-nueva-btn--ghost" onClick={onHide}>
          Cerrar
        </button>
        <button
          type="button"
          className={`citas-nueva-btn ${conflictos.length > 0 ? 'citas-nueva-btn--warn' : 'citas-nueva-btn--primary'}`}
          onClick={onSave}
          disabled={!canSave}
        >
          <i className={`bi ${conflictos.length > 0 ? 'bi-exclamation-triangle' : 'bi-calendar-check'} me-2`} aria-hidden />
          {conflictos.length > 0 ? 'Guardar de todas formas' : (isEdit ? 'Guardar nuevo horario' : 'Confirmar cita')}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
